/**
 * @typedef {Object} SaveComment - Formato esperado pela API para salvar o comentário da etapa atual
 * @property {string} comentario - O texto do comentário
 * @property {number} [revisor] - ID do revisor (opcional)
 * @property {boolean|null} [status=null] - Status do comentário (opcional, valor padrão null)
 */

/**
 * @typedef {Object} Comment - Comentario retornado pela API
 * @property {number} workflow_id - ID da etapa que o comentário foi feito
 * @property {string} workflow_nome - Nome da etapa que o comentário foi feito
 * @property {string} comentario - O texto do comentário
 * @property {number} responsavel_id - ID de responsável de quem comentou
 * @property {Object} [responsavel] - Objeto com dados de responsável de quem comentou
 * @property {string} responsavel.nome - Nome de quem comentou
 * @property {string} responsavel.email - E-mail de quem comentou
 * @property {string} data - data ISO do comentário
 */

export const SYS_LOG_COMMENT_KEY = 'Sistema: ';

// Identifica se é um comentário do sistema ou não
const rg = new RegExp(`^(${SYS_LOG_COMMENT_KEY}).*`);

/**
 * Converte a mensagem desejada no formato esperado para um comentário automático do fluxo de Workflow e não um comentário do usuário
 * @param {string} comment - descrição da ação realizada
 * @returns {string} - retorna a mensagem para ser enviada para a API
 */
export const createSysComment = (comment) => `${SYS_LOG_COMMENT_KEY}${comment}`;

/**
 * Converte um comentário automático do fluxo de Workflow em um comentário legivel para o usuário
 * @param {string} sysComment - comentário do sistema
 * @returns {string|undefined} - retorna uma mensagem amigavel ou `undefined` caso não seja um Sys Comment
 */
export const parseSysComment = (sysComment) => {
  if (typeof sysComment !== 'string' || !rg.test(sysComment)) {
    return undefined;
  }

  const target = new RegExp(SYS_LOG_COMMENT_KEY, 'g');
  return sysComment.replace(target, '');
};

/**
 * Separa os comentários em dois grupos, os comentários dos usuários e os comentários do sistema
 * @param {Comment[]} comments - lista de comentários de um BP retornados pela API
 * @returns {[Comment[], Comment[]]} - retorna um tuple de dois arrays, sendo o primeiro comentários dos usuários e o segundo do sistema
 */
export const partitionComments = (comments) => {
  if (!Array.isArray(comments)) {
    return [[], []];
  }

  const coms = [];
  const sysComs = [];
  comments.forEach((com) => {
    if (rg.test(com.comentario)) {
      sysComs.push(com);
    } else {
      coms.push(com);
    }
  });

  return [coms, sysComs];
};

/**
 * Filtra todos os comentários do sistema. Método compativel com Array.filter.
 * @param {Comment} sysComment - comentário atual sendo analisado
 * @returns {boolean} - retorna se o comentário é do sistema ou não
 */
export const filterSysComs = (sysComment) => rg.test(sysComment.comentario);

/**
 * Filtra todos os comentários que não são do sistema. Método compativel com Array.filter.
 * @param {Comment} comment - comentário atual sendo analisado
 * @returns {boolean} - retorna se o comentário não é do sistema
 */
export const filterSysComOut = (comment) => !rg.test(comment.comentario);
